import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentHYCSSettingUpData,
  type EnrollmentHYCSSettingUpSubmitInput,
  type EnrollmentHYCSSettingUpSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment HYCS SettingUp data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSSettingUpData>
 * @example const response = await getEnrollmentHYCSSettingUpData()
 */
export const getEnrollmentHYCSSettingUpData = async (
  context?: DefaultContext,
): Promise<EnrollmentHYCSSettingUpData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentHYCSSettingUpData',
      query: `mutation getEnrollmentHYCSSettingUpData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSSettingUpData }
  return response.submitEnrollment
}

export const useGetEnrollmentHYCSSettingUpData = createQueryHook<undefined, EnrollmentHYCSSettingUpData>(
  getEnrollmentHYCSSettingUpData,
)

/**
 * Submit Enrollment HYCS SettingUp data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSSettingUpSubmitOutput>
 * @example const response = await submitEnrollmentHYCSSettingUpData();
 */
export const submitEnrollmentHYCSSettingUpData = async (
  enrollmentSettingUpSubmitInput: EnrollmentHYCSSettingUpSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSSettingUpSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentHYCSSettingUpData',
      query: `mutation submitEnrollmentHYCSSettingUpData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentSettingUpSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSSettingUpSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentHYCSSettingUpData = createMutationHook<
  EnrollmentHYCSSettingUpSubmitInput,
  EnrollmentHYCSSettingUpSubmitOutput
>(submitEnrollmentHYCSSettingUpData)
